/*
 * src/components/ClubLogo.tsx
 *
 * This file defines the ClubLogo component, which is responsible for
 * fetching and displaying a club's logo image. It takes a clubId as a prop,
 * retrieves the logo URL from the clubs endpoint, and renders
 * an <img> tag. It includes a fallback to a placeholder image if the
 * logo URL is not found or if there's an error loading the image.
 */
import { useState, useEffect } from 'react';
import { getClubLogoApi } from '../lib/clubsApi';

/**
 * Props for the ClubLogo component.
 */
export interface ClubLogoProps {
  /** The unique identifier of the club whose logo is to be displayed. */
  clubId: string;
  className?: string;
}

/**
 * ClubLogo is a React component that fetches and displays a club's logo.
 * It takes a `clubId`, looks up the `logo_url` via the backend,
 * and renders an image. If the logo URL is not found or an error occurs
 * during image loading, it defaults to a placeholder image.
 */
export default function ClubLogo({ clubId, className }: ClubLogoProps) {
  const [logoUrl, setLogoUrl] = useState<string>("/hc-placeholder.png");

  useEffect(() => {
    const sync = (event: Event) => {
      const detail = (event as CustomEvent<{ clubId: string; logoUrl: string }>).detail;
      if (detail.clubId === clubId) setLogoUrl(detail.logoUrl);
    };
    window.addEventListener('club-logo-updated', sync);
    return () => window.removeEventListener('club-logo-updated', sync);
  }, [clubId]);

  useEffect(() => {
    if (!clubId) {
      setLogoUrl('/hc-placeholder.png');
      return;
    }

    const loadClubLogo = async () => {
      try {
        const club = await getClubLogoApi(clubId);
        setLogoUrl(club?.logo_url || '/hc-placeholder.png');
      } catch {
        setLogoUrl('/hc-placeholder.png');
      }
    };

    loadClubLogo();
  }, [clubId]);

  return (
    <img
      src={logoUrl}
      alt="Club logo"
      className={`w-6 h-6 rounded ${className || ''}`}
      onError={(e) => {
        e.currentTarget.src = '/hc-placeholder.png';
      }}
    />
  );
}
